/**
 * usePermissions Hook
 *
 * Centralized permission checking for role-based access control.
 * Extracts permission logic into a lightweight hook that can be used
 * anywhere without loading member data.
 *
 * @module hooks/usePermissions
 */

import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import type { OrgRole } from '@/lib/firebase-types';

// =============================================================================
// Types
// =============================================================================

export interface Permissions {
  /** Current user's role in the organization */
  currentUserRole: OrgRole | null;

  /** Can create and revoke API keys (owner, org_admin, billing_admin, admin, manager) */
  canManageApiKeys: boolean;

  /** Can manage team members (owner, org_admin, billing_admin, admin, manager) */
  canManageTeam: boolean;

  /** Can access billing settings (owner, org_admin, billing_admin) */
  canAccessBilling: boolean;

  /** Can update organization settings like name (owner, org_admin) */
  canUpdateOrgSettings: boolean;

  /** Can transfer ownership to another member (owner only) */
  canTransferOwnership: boolean;

  /** Can delete the organization (owner only) */
  canDeleteOrganization: boolean;

  /** Has access to any settings page (canUpdateOrgSettings || canManageTeam || canAccessBilling) */
  hasAnySettingsAccess: boolean;

  /** Whether permissions are still loading */
  loading: boolean;
}

// =============================================================================
// Constants
// =============================================================================

/** Roles that can manage API keys */
const API_KEY_MANAGERS: OrgRole[] = ['owner', 'org_admin', 'billing_admin', 'admin', 'manager'];

/** Roles that can manage team members */
const TEAM_MANAGERS: OrgRole[] = ['owner', 'org_admin', 'billing_admin', 'admin', 'manager'];

/** Roles that can access billing */
const BILLING_ACCESSORS: OrgRole[] = ['owner', 'org_admin', 'billing_admin'];

/** Roles that can update org settings */
const ORG_SETTINGS_MANAGERS: OrgRole[] = ['owner', 'org_admin'];

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook to check user permissions within the current organization.
 *
 * @returns Permission flags based on the user's role
 *
 * @example
 * ```tsx
 * function BillingButton() {
 *   const { canAccessBilling } = usePermissions();
 *
 *   if (!canAccessBilling) {
 *     return <Button disabled>Billing (Admin only)</Button>;
 *   }
 *
 *   return <Button onClick={openBilling}>Billing</Button>;
 * }
 * ```
 */
export function usePermissions(): Permissions {
  const { user, organization, loading: authLoading } = useAuth();

  const permissions = useMemo((): Permissions => {
    // Determine current user's role in the organization
    const currentUserRole: OrgRole | null = (() => {
      if (!user || !organization) return null;
      const membership = user.orgMemberships?.find((m) => m.orgId === organization.id);
      return membership?.role || null;
    })();

    // Compute permission flags
    const canManageApiKeys = API_KEY_MANAGERS.includes(currentUserRole as OrgRole);
    const canManageTeam = TEAM_MANAGERS.includes(currentUserRole as OrgRole);
    const canAccessBilling = BILLING_ACCESSORS.includes(currentUserRole as OrgRole);
    const canUpdateOrgSettings = ORG_SETTINGS_MANAGERS.includes(currentUserRole as OrgRole);
    const canTransferOwnership = currentUserRole === 'owner';
    const canDeleteOrganization = currentUserRole === 'owner';
    const hasAnySettingsAccess = canUpdateOrgSettings || canManageTeam || canAccessBilling;

    return {
      currentUserRole,
      canManageApiKeys,
      canManageTeam,
      canAccessBilling,
      canUpdateOrgSettings,
      canTransferOwnership,
      canDeleteOrganization,
      hasAnySettingsAccess,
      loading: authLoading,
    };
  }, [user, organization, authLoading]);

  return permissions;
}
